import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, DateInput, FormField, Text, TextInput } from "grommet";
import * as React from "react";
import Avatar from "react-avatar";
import StyledModal from "../components/StyledModel";
import { useReferenceData } from "../reference/ReferenceStore";
import { TeamViewModel } from "../teams/TeamsStore";
import { AddMatchOutcomeRequest, addOutcome, CreateMatchRequest, MatchOutcomeViewModel, MatchParticipantViewModel, MatchViewModel, OutcomeType, RemoveMatchOutcomeRequest, removeOutcome } from "./MatchesStore";
import { Outcome, OutcomeColor, OutcomeIcon } from "./OutcomeIcon";
import { RenderOutcome } from "./OutcomeRenderer";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;

interface AdditionalOutcomeRequest {
    team: TeamViewModel;
    request: AddMatchOutcomeRequest;
}

export const MatchParticipantOutcomes = ({ match, disabled, participant, handleChange }: { match: MatchViewModel; disabled: boolean; participant: MatchParticipantViewModel; handleChange: Function }) => {
    const [showAddOutcomeModal, setShowAddOutcomeModal] = React.useState(false);
    const [showRemoveOutcomeModal, setShowRemoveOutcomeModal] = React.useState(false);

    const [participantOutcome, setParticipantOutcome] = React.useState({} as MatchParticipantViewModel);
    const [outcomeToRemove, setOutcomeToRemove] = React.useState({} as MatchOutcomeViewModel);

    const outcomeTypes = useReferenceData("outcomeTypes") as OutcomeType[];

    const [description, setDescription] = React.useState("");
    const [outcomeType, setOutcomeType] = React.useState("");
    const [score, setScore] = React.useState(0);

    const [outcomeCreateError, setOutcomeCreateError] = React.useState("");
    const [outcomeRemoveError, setOutcomeRemoveError] = React.useState("");

    const [isLoading, setIsLoading] = React.useState(false);

    const [additionalOutcomes, setAdditionalOutcomes] = React.useState([] as AdditionalOutcomeRequest[]);

    const startAddOutcome = (participant: MatchParticipantViewModel) => {
        setParticipantOutcome(participant);
        setShowAddOutcomeModal(true);
    };

    const startChangeOutcome = async (participant: MatchParticipantViewModel) => {
        // Remove existing outcomes first before allowing change
        if (participant.outcomes.length > 0) {
            setOutcomeCreateError("");
            setIsLoading(true);
            try {
                const removePromises = participant.outcomes.map(outcome => 
                    removeOutcome({ matchID: match.id, outcomeID: outcome.id } as RemoveMatchOutcomeRequest)
                );
                const responses = await Promise.all(removePromises);
                if (responses.every(r => r.success)) {
                    handleChange();
                    setParticipantOutcome(participant);
                    setShowAddOutcomeModal(true);
                } else {
                    setOutcomeCreateError("Failed to remove existing outcome");
                }
            } catch (e) {
                setOutcomeCreateError("Failed to remove existing outcome");
            } finally {
                setIsLoading(false);
            }
        } else {
            startAddOutcome(participant);
        }
    };

    const addOutcomeToMatch = () => {
        setOutcomeCreateError("");
        setIsLoading(true);
        addOutcome({ matchID: match.id, description, outcomeType, participantID: participantOutcome.id, score } as AddMatchOutcomeRequest).then(async (response) => {
            if (response.success) {
                let additionalResponses = await Promise.all(additionalOutcomes.map((additionalOutcome) => addOutcome(additionalOutcome.request)));

                if (additionalResponses.every((response) => response.success)) {
                    setDescription("");
                    setOutcomeType("");
                    setScore(0);
                    handleChange();
                    setShowAddOutcomeModal(false);
                    setAdditionalOutcomes([]);
                } else {
                    setOutcomeCreateError(additionalResponses.map((r) => r.errors.join("\n")).join("\n"));
                }
            } else {
                setOutcomeCreateError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const startRemoveOutcome = (outcome: MatchOutcomeViewModel) => {
        setOutcomeToRemove(outcome);
        setShowRemoveOutcomeModal(true);
    };

    const removeOutcomeFromMatch = () => {
        setOutcomeRemoveError("");
        setIsLoading(true);
        removeOutcome({ matchID: match.id, outcomeID: outcomeToRemove.id } as RemoveMatchOutcomeRequest).then((response) => {
            if (response.success) {
                handleChange();
                setShowRemoveOutcomeModal(false);
            } else {
                setOutcomeRemoveError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const setOutcome = (outcomeType: OutcomeType) => {
        let addOutcomes = [] as any[];
        if (outcomeType.name == "Win") {
            match.participants.forEach((p) => {
                if (p.id != participant.id && p.outcomes.length == 0) {
                    addOutcomes.push({
                        team: p.team,
                        request: {
                            matchID: match.id,
                            description: "",
                            participantID: p.id,
                            outcomeType: "Loss",
                            score: 0,
                        } as AddMatchOutcomeRequest,
                    } as AdditionalOutcomeRequest);
                }
            });
            setAdditionalOutcomes([...additionalOutcomes, ...addOutcomes]);
        } else {
            setAdditionalOutcomes([]);
        }
        setOutcomeType(outcomeType.name);
    };

    const updateAdditionalOutcome = (index, outcome) => {
        let copy = [...additionalOutcomes];

        copy[index] = { ...copy[index], request: { ...copy[index].request, ...outcome } };

        setAdditionalOutcomes(copy);
    };

    return (
        <Box direction="row" gap="small" align="center">
            {participant.outcomes.map((outcome, outcomeIndex) => (
                <span key={"s" + outcomeIndex}>
                    <Outcome outcome={outcome} disabled={disabled} key={outcomeIndex} onClick={() => startRemoveOutcome(outcome)}></Outcome>
                    {outcome.description}
                </span>
            ))}
            
            {/* Show Add or Change button based on whether outcomes exist */}
            {participant.outcomes.length === 0 ? (
                <Button label="Add Outcome" icon={plus} onClick={() => startAddOutcome(participant)} disabled={disabled || isLoading}></Button>
            ) : (
                <Button label="Change Outcome" icon={plus} onClick={() => startChangeOutcome(participant)} disabled={disabled || isLoading} size="small"></Button>
            )}

            <StyledModal isOpen={showAddOutcomeModal} setIsOpen={setShowAddOutcomeModal}>
                <Box width={"medium"}>
                    <Box direction="row" align="center" gap="small">
                        <img src={participantOutcome?.team?.logoUri} alt="" style={{ height: "auto", maxWidth: "80px", width: "auto", maxHeight: "80px", objectFit: "contain" }} />
                        {participantOutcome?.team?.name}
                    </Box>
                    <Box gap="small" pad="small">
                        {outcomeTypes.map((_outcomeType, outcomeTypeIndex) => (
                            <Button
                                key={outcomeTypeIndex}
                                primary={outcomeType == _outcomeType.name}
                                color={OutcomeColor({ outcomeType: _outcomeType.name })}
                                label={_outcomeType.displayName}
                                icon={OutcomeIcon({ outcomeType: _outcomeType.name })}
                                onClick={() => setOutcome(_outcomeType)}
                                disabled={isLoading}
                            ></Button>
                        ))}
                    </Box>

                    {/* <FormField label="Outcome">
                                    <Select
                                        value={outcomeType}
                                        onChange={(e) => setOutcomeType(e.target.value)}
                                        labelKey="displayName"
                                        disabled={isUpdating}
                                        options={outcomeTypes}
                                        children={(option, index, options, { active, disabled, selected }) => (
                                            <Box pad="1rem">
                                                <Text>{option.displayName}</Text>
                                            </Box>
                                        )}
                                        valueKey={{ key: "name", reduce: true }}
                                    />
                                </FormField> */}
                    <FormField label="Description">
                        <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
                    </FormField>
                    <FormField label="Score">
                        <TextInput type={"number"} value={score} onChange={(e) => setScore(+e.target.value)} />
                    </FormField>
                    {additionalOutcomes.map(({ team, request }, i) => (
                        <div key={i}>
                            <Box direction="row">
                                <Avatar round="5px" size="45px" src={team.logoUri}></Avatar>
                                <RenderOutcome outcome={{ outcomeType: request.outcomeType }} size="medium"></RenderOutcome>
                                <TextInput type={"number"} value={request.score} onChange={(e) => updateAdditionalOutcome(i, { score: +e.target.value })} />
                            </Box>
                            <TextInput value={request.description} onChange={(e) => updateAdditionalOutcome(i, { description: e.target.value })} />
                        </div>
                    ))}
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{outcomeCreateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowAddOutcomeModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => addOutcomeToMatch()} label="Add" disabled={isLoading || !outcomeType}></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveOutcomeModal} setIsOpen={setShowRemoveOutcomeModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this outcome?</Text>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{outcomeRemoveError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveOutcomeModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => removeOutcomeFromMatch()} label="Remove" color="status-critical" disabled={isLoading}></Button>
                    </Box>
                </Box>
            </StyledModal>
        </Box>
    );
};

export default MatchParticipantOutcomes;
